import * as React from "react";
import { useState } from "react";
import {
  Button,
  Divider,
  Text,
  Title1,
  Title2,
  Title3,
  Card,
  CardHeader,
  CardPreview,
  CardFooter,
  makeStyles,
  InfoLabel,
  InfoLabelProps,
  Link,
} from "@fluentui/react-components";
import { getAccountData } from "../taskpane";

/**
 * Interface définissant les propriétés du composant Home.
 */
interface HomeProps {
  setCurrentView: (view: string) => void;
}

/**
 * Composant Home : affiche la page d'accueil avec des informations sur l'utilisateur,
 * les crédits restants, et les assistants disponibles.
 * @param {HomeProps} props - Propriétés du composant.
 * @returns {JSX.Element} - Le composant React.
 */
const Home: React.FC<HomeProps> = (props: HomeProps) => {
  const [username, setUsername] = useState(""); // Nom de l'utilisateur
  const [requestsLeft, setRequestsLeft] = useState(-1); // Nombre de crédits restants
  const styles = useStyles();

  /**
   * Récupère les données utilisateur (nom et crédits restants).
   */
  async function getUser() {
    try {
      const user = await getAccountData();
      setUsername(user.username);
      setRequestsLeft(user.requests_left);
    } catch (error) {
      console.error(error);
    }
  }

  // Liste des assistants disponibles avec leurs informations
  const assistants = [
    {
      name: "Extraction d'informations",
      priceTag: "5 à 10 crédits en moyenne",
      view: "ExtractionPage",
    },
    {
      name: "Uniformiser des données",
      priceTag: "5 à 10 crédits en moyenne",
      view: "StandardisationPage",
    },
    {
      name: "Génération de formules",
      priceTag: "1 à 3 crédits en moyenne",
      view: "ChatPage",
    },
    {
      name: "Guide du manuel",
      priceTag: "50 à 200 crédits en moyenne",
      view: "GuideLivre",
    },
  ];

  // Charge les données utilisateur au montage du composant
  React.useEffect(() => {
    getUser();
  }, []);

  // Rendu du composant
  return (
    <div className={styles.container}>
      {/* En-tête avec le nom de l'utilisateur et les crédits restants */}
      <div className={styles.headerContainer}>
        <Title2 align="center">Bienvenue,</Title2>
        <Title1 align="center">{username}</Title1>
        <div>
          <Text style={{ color: requestsLeft === 0 ? "red" : "grey" }}>
            {requestsLeft} crédits en banque.
          </Text>
          {/* Bouton InfoLabel pour acheter plus de crédits */}
          <InfoLabel
            info={
              <>
                Chaque assistant a un prix de base qui dépend de ses capacités et un prix basé sur
                la profondeur du contexte fournit par votre requête.{" "}
                <Link
                  as="button"
                  onClick={() => props.setCurrentView("CreditPage")}
                  style={{
                    cursor: "pointer",
                    textDecoration: "underline",
                    background: "none",
                    border: "none",
                    fontSize: "12px",
                  }}
                >
                  Acheter plus de crédits
                </Link>
              </>
            }
            {...props}
          ></InfoLabel>
        </div>
        <Divider className={styles.dividerMargin} inset />
      </div>

      {/* Section pour l'assistant général */}
      <Title3 className={styles.sectionMargin} align="center">
        Essayez l'assistant IA le plus puissant sur le marché!
      </Title3>
      <Card className={`${styles.card} ${styles.sectionMargin}`}>
        <CardHeader
          header={<Title3 style={{ textAlign: "center" }}>Assistant général</Title3>}
          description={<Text style={{ color: "grey" }}>60 à 200 crédits en moyenne</Text>}
          className={styles.cardHeader}
        />
        <CardFooter className={styles.footer}>
          <Button onClick={() => props.setCurrentView("GeneralAssistant")}>Essayez-le!</Button>
        </CardFooter>
      </Card>

      <Divider className={styles.dividerMargin} />

      <Title3 className={styles.sectionMargin} align="center">
        Ou essayez certains de nos assistants plus spécialisés
      </Title3>

      {/* Section pour les assistants spécialisés */}
      <div className={`${styles.horizontalCardsContainer} ${styles.sectionMargin}`}>
        {assistants.map((assistant) => (
          <Card
            onClick={() => props.setCurrentView(assistant.view)}
            key={assistant.name}
            className={styles.horizontalCard}
            orientation="horizontal"
          >
            <CardHeader
              header={<Text weight="semibold">{assistant.name}</Text>}
              description={<Text style={{ color: "grey" }}>{assistant.priceTag}</Text>}
            />
            <CardFooter className={styles.horizontalCardImageContainer}>
              <img
                className={styles.horizontalCardImage}
                src="assets/img_logo_agrandi.png"
                alt={assistant.name}
              />
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Home;

const useStyles = makeStyles({
  container: {
    minWidth: "400px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    height: "auto",
    overflowY: "auto",
  },
  headerContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginTop: "1vh",
  },
  topLine: {
    width: "100%",
    height: "2vh",
    backgroundColor: "#1e6d43",
  },
  card: {
    margin: "0 auto",
    width: "80vw",
    height: "250px",
  },
  footer: {
    display: "flex",
    justifyContent: "center",
  },
  horizontalCardsContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: "10px",
    gap: "10px",
    flexWrap: "wrap",
  },
  horizontalCard: {
    minWidth: "400px",
    width: "80vw",
    paddingLeft: "20px",
  },
  horizontalCardImageContainer: {
    display: "flex",
    justifyContent: "flex-end",
  },
  horizontalCardImage: {
    maxWidth: "60px",
    maxHeight: "60px",
    objectFit: "contain",
  },
  alignRight: {
    marginLeft: "auto",
    marginRight: "1wh",
  },
  cardHeader: {
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
  },

  sectionMargin: {
    marginTop: "10px",
    marginBottom: "10px",
  },
  dividerMargin: {
    marginTop: "10px",
    marginBottom: "10px",
  },
});
